import React, { useState, useEffect } from 'react';
import { Task, User, api } from '../lib/api';
import { TaskList } from './TaskList';
import { SimpleBadge } from './ui/simple-badge';

interface GlobalTaskViewProps {
  userId: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  onReassign?: (taskId: string, userId: string) => void;
  teamMembers?: User[];
}

type TaskTab = 'today' | 'upcoming' | 'overdue' | 'completed';

export const GlobalTaskView: React.FC<GlobalTaskViewProps> = ({ userId, dueDateFrom, dueDateTo, onReassign, teamMembers }) => {
  const [activeTab, setActiveTab] = useState<TaskTab>('today');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    loadTasks();
  }, [userId, dueDateFrom, dueDateTo]);

  const loadTasks = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const allTasks = await api.getUserTasks(userId, undefined, undefined, dueDateFrom, dueDateTo);
      setTasks(allTasks);
    } catch (err) {
      console.error('Failed to load tasks:', err);
      setError('Failed to load tasks. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (taskId: string, status: Task['status']) => {
    try {
      // Map Task status to API-compatible status
      let apiStatus: 'PENDING' | 'COMPLETED' | 'OVERDUE';
      switch (status) {
        case 'IN_PROGRESS':
          apiStatus = 'PENDING';
          break;
        case 'CANCELLED':
          apiStatus = 'PENDING'; // Map cancelled to pending for API
          break;
        case 'COMPLETED':
          apiStatus = 'COMPLETED';
          break;
        case 'PENDING':
        default:
          apiStatus = 'PENDING';
          break;
      }
      
      await api.updateTask(taskId, { status: apiStatus });
      setTasks(prev => prev.map(task =>
        task.id === taskId ? { ...task, status } : task
      ));
      if (status === 'COMPLETED') {
        showToast('Task marked as completed', 'success');
      }
    } catch (err) {
      console.error('Failed to update task:', err);
      showToast('Failed to update task. Please try again.', 'error');
      loadTasks();
    }
  };

  const handleDelete = async (taskId: string) => {
    try {
      await api.deleteTask(taskId);
      setTasks(prev => prev.filter(task => task.id !== taskId));
      showToast('Task deleted successfully', 'success');
    } catch (err) {
      console.error('Failed to delete task:', err);
      showToast('Failed to delete task. Please try again.', 'error');
      loadTasks();
    }
  };

  const handleCreate = async (taskData: any) => {
    try {
      // Use standalone task API for creating tasks from global view
      // This allows creating tasks without a lead, or with an optional leadId
      const newTask = await api.createStandaloneTask({
        title: taskData.title,
        description: taskData.description,
        dueDate: taskData.dueDate,
        priority: taskData.priority,
        leadId: taskData.leadId || undefined,
      });
      setTasks(prev => [...prev, newTask]);
      showToast('Task created successfully', 'success');
    } catch (err) {
      console.error('Failed to create task:', err);
      showToast('Failed to create task. Please try again.', 'error');
      loadTasks();
    }
  };

  const getFilteredTasks = (tab: TaskTab): Task[] => {
    const today = new Date();
    const todayString = today.toDateString();
    
    return tasks.filter(task => {
      const taskDate = new Date(task.dueDate);
      const taskDateString = taskDate.toDateString();
      const isOverdue = taskDate < today && task.status !== 'COMPLETED';
      
      switch (tab) {
        case 'today':
          return taskDateString === todayString && task.status !== 'COMPLETED';
        case 'upcoming':
          return taskDate > today && task.status !== 'COMPLETED';
        case 'overdue':
          return isOverdue;
        case 'completed':
          return task.status === 'COMPLETED';
        default:
          return true;
      }
    });
  };

  const getTabStats = () => {
    const today = new Date();
    const todayString = today.toDateString();
    
    return {
      today: tasks.filter(t => new Date(t.dueDate).toDateString() === todayString && t.status !== 'COMPLETED').length,
      upcoming: tasks.filter(t => new Date(t.dueDate) > today && t.status !== 'COMPLETED').length,
      overdue: tasks.filter(t => new Date(t.dueDate) < today && t.status !== 'COMPLETED').length,
      completed: tasks.filter(t => t.status === 'COMPLETED').length
    };
  };

  const stats = getTabStats();

  const tabs: { key: TaskTab; label: string; icon: string; count: number }[] = [
    { key: 'today', label: 'Today', icon: '🔹', count: stats.today },
    { key: 'upcoming', label: 'Upcoming', icon: '🟡', count: stats.upcoming },
    { key: 'overdue', label: 'Overdue', icon: '🔴', count: stats.overdue },
    { key: 'completed', label: 'Completed', icon: '✅', count: stats.completed }
  ];

  if (error) {
    return (
      <div className="text-center py-8" role="alert">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={loadTasks}
          aria-label="Retry loading tasks"
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toast notification */}
      <div aria-live="polite" className="fixed top-4 right-4 z-50">
        {toast && (
          <div className={`px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all ${
            toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`} role="status">
            {toast.message}
          </div>
        )}
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Task Management</h1>
          <p className="text-gray-600 mt-1">Manage your tasks and follow-ups</p>
        </div>

        <div className="flex items-center space-x-2 flex-shrink-0">
          <SimpleBadge className="bg-blue-100 text-blue-800 border-blue-200">
            {tasks.length} Total Tasks
          </SimpleBadge>
          {stats.overdue > 0 && (
            <SimpleBadge className="bg-red-100 text-red-800 border-red-200">
              {stats.overdue} Overdue
            </SimpleBadge>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto" role="tablist" aria-label="Task categories">
          {tabs.map(tab => (
            <button
              key={tab.key}
              role="tab"
              aria-selected={activeTab === tab.key}
              aria-controls={`tabpanel-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 whitespace-nowrap ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
              <SimpleBadge className={`text-xs ${
                activeTab === tab.key
                  ? 'bg-blue-100 text-blue-800 border-blue-200'
                  : 'bg-gray-100 text-gray-600 border-gray-200'
              }`}>
                {tab.count}
              </SimpleBadge>
            </button>
          ))}
        </nav>
      </div>

      {/* Task List */}
      <div className="bg-white rounded-lg shadow" role="tabpanel" id={`tabpanel-${activeTab}`} aria-label={`${tabs.find(t => t.key === activeTab)?.label} tasks`}>
        <div className="p-4 sm:p-6">
          <TaskList
            tasks={getFilteredTasks(activeTab)}
            onStatusChange={handleStatusChange}
            onDelete={handleDelete}
            onCreate={handleCreate}
            userId={userId}
            showLeadInfo={true}
            title={`${tabs.find(t => t.key === activeTab)?.label} Tasks`}
            isLoading={isLoading}
            onReassign={onReassign}
            teamMembers={teamMembers}
          />
        </div>
      </div>
    </div>
  );
}; 